import { Tab, Tabs }                                                                                from '@mui/material';
import React, { ChangeEvent, FC, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
}                                                                                                   from 'react-router-dom';
import styled
  from 'styled-components';
import AuthorizationContext
  from '../utils/withAuthorization/authorizationContext';
import { TFamilyAccountType }                                                                       from '../utils/withAuthorization/withAuthorization';

// HiddenTab is used to prevent console error when
// current route doesn't match any of the tabs values
const HiddenTab = styled(Tab)`&& {
  display: none;
}`;

export interface IRouterDrivenTab {
  link: string;
  label: ReactNode;
  icon?: ReactElement;
  color?: string;
  component: ReactElement;
  onClose?: (link: string) => void;
  roles?: TFamilyAccountType[];
}

interface IRouterDrivenTabsProps {
  tabs: IRouterDrivenTab[];
  baseURL: string;
  centered?: boolean;
}

const StyledTabs = styled(Tabs)`
  .MuiTabs-indicator {
    height: 3px;
    background-color: #fac05e;
  }

  /* Hide scroll buttons on larger screens */
  .MuiTabs-scrollButtons {
    display: none;
  }
  .MuiTabs-scrollButtons.Mui-disabled {
    width: 15px;
  }

  /* Show scroll buttons on mobile */
  @media (max-width: 768px) {
    .MuiTabs-scrollButtons {
      display: inline-flex !important;
    }
  }
`;

const StyledTab = styled(Tab)`
  && {
    font-family: 'Jost';
    opacity: 1;
    font-weight: 600;
    font-size: 22px;
    line-height: 22px;
    letter-spacing: 1px;
    font-stretch: normal;
    min-width: unset;
    padding-left: 0;
    padding-right: 0;
    margin-right: 24px;

    &.Mui-selected {
      color: #2c3f69 !important;
      font-weight: bold;
    }

    @media (max-width: 768px) {
      font-size: 18px;
      margin-right: 30px;
    }

    @media (max-width: 1024px) {
      font-size: 20px;
    }
  }
`;

export const RouterDrivenTabs: FC<IRouterDrivenTabsProps> = ({
  tabs,
  baseURL,
  centered = false,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const page = pathname.replace(`${ baseURL }/`, '');
  const user = useContext(AuthorizationContext);

  const [ filteredTabs, setFilteredTabs ] = useState<IRouterDrivenTab[]>([]);

  const currentTab = useMemo(
    () => [ ...tabs ].reverse().find(tab => page.indexOf(tab.link) >= 0),
    [ page, tabs ]
  );

  const handleChangeTab = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-types
    (_event: ChangeEvent<{}>, tabLink: string) => {
      navigate(`${ baseURL }/${ tabLink }`);
    },
    [ baseURL, navigate, pathname ]
  );

  const a11yProps = useCallback(
    (tabLink: string) => ({
      id              : `tab-${ tabLink }`,
      'aria-controls' : `tabpanel-${ tabLink }`,
    }),
    []
  );

  const tab = currentTab?.component;

  useEffect(() => {
    if (filteredTabs) {
      setFilteredTabs(tabs.filter(({ roles }) => !roles || !!roles?.find(role => role === user?.data?.accountType)));
    }
  }, [ tabs, user?.data?.accountType ]);

  useEffect(() => {
    if (filteredTabs && filteredTabs.length) {
      const map: { [key: string]: IRouterDrivenTab } = {};
      let isTabNameExists = false;
      let tabWithSameName: any = currentTab;

      filteredTabs.forEach(tab => {
        map[tab.link as string] = tab;

        if (!isTabNameExists && tab.label === currentTab?.label) {
          isTabNameExists = true;
          tabWithSameName = tab;
        }
      });

      const isTabExists = !!map[currentTab?.link as string];

      if (!isTabNameExists) {
        navigate(`${ baseURL }/${ filteredTabs[0]?.link }`);
      } else if (isTabNameExists && !isTabExists) {
        if (tabWithSameName?.link.indexOf('undefined') < 0) {
          navigate(`${ baseURL }/${ tabWithSameName?.link }`);
        }
      }
    }
  }, [ filteredTabs ]);

  return (
    <>
      <StyledTabs
        value={currentTab ? currentTab.link : ''}
        centered={centered}
        scrollButtons="auto"
        variant="scrollable"
        TabIndicatorProps={{
          style: {
            height: '3px',
          },
        }}
        onChange={handleChangeTab}
      >

        { filteredTabs.map(
          (tab, index) =>
            (<StyledTab
              key={`${ tab.link }-${ index }`}
              {...a11yProps(tab.link)}
              disableRipple
              icon={tab.icon}
              label={tab.label}
              value={tab.link}
            />)
        ) }
      </StyledTabs>

      { tab }
    </>
  );
};
